import { useEffect, useState, useCallback } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { Api } from "../../api/Assinafy"
import { Alert } from "../../components"
import { Page, Section } from "../../layout"
import { isIdentityVerificationMethod } from "../../utils/identityVerification"
import { resolvePostVerificationStatus, resolveSigningLinkStatus } from "../Document/signingLinkStatusUtils"

const signerDocumentFromPayload = (data) => {
  if (!data) {
    return null
  }
  return Array.isArray(data) ? data[0] : data
}

const SignerVerificationWithCode = () => {
  const { signerAccessCode, code } = useParams()
  const navigate = useNavigate()
  const [formError, setFormError] = useState({})
  const [loading, setLoading] = useState(true)
  const [signer, setSigner] = useState(null)

  const getNextStep = useCallback((signerData) => {
    if (!signerData) {
      return `/sign/${signerAccessCode}`
    }

    const { verification_method, has_accepted_terms } = signerData

    if (!verification_method) {
      return `/sign/${signerAccessCode}`
    }

    if ((verification_method === "Email" || verification_method === "Bypass") && !has_accepted_terms) {
      return `/sign/${signerAccessCode}`
    }

    if (has_accepted_terms && signerData.id) {
      return new Promise((resolve) => {
        Api("signer/document")(signerData.id, signerAccessCode)
          .then(({ status: docStatus, data: { data } }) => {
            const doc = signerDocumentFromPayload(data)
            if (docStatus === 200 && doc && (doc.id || doc.document_id)) {
              const documentId = doc.id || doc.document_id
              resolve(`/sign/${signerAccessCode}/confirm-data/${documentId}`)
            } else {
              resolve(`/sign/${signerAccessCode}`)
            }
          })
          .catch(() => {
            resolve(`/sign/${signerAccessCode}`)
          })
      })
    }

    return `/sign/${signerAccessCode}`
  }, [signerAccessCode])

  const handleBypassFlow = useCallback((signerData) => {
    if (signerData.id) {
      Api("signer/document")(signerData.id, signerAccessCode)
        .then(({ status: docStatus, data: { data } }) => {
          const doc = signerDocumentFromPayload(data)
          if (docStatus === 200 && doc && (doc.id || doc.document_id)) {
            const documentId = doc.id || doc.document_id
            const documentName = doc.name || signerData.document_name || "documento"

            if (documentId) {
              navigate(`/sign/${signerAccessCode}/confirm-data/${documentId}`, {
                state: {
                  verified: true,
                  dataConfirmed: false,
                  documentId: documentId,
                  documentName: documentName,
                  ownerName: signerData.sender_name || "Proprietário do documento",
                  verification_method: "Bypass",
                },
                replace: true,
              })
            }
          }
        })
        .catch(() => {
          setLoading(false)
        })
    }
  }, [signerAccessCode, navigate])

  const handleVerificationSuccess = useCallback((signerData) => {
    if (signerData?.id) {
      Api("signer/document")(signerData.id, signerAccessCode)
        .then(({ status: docStatus, data: { data } }) => {
          const doc = signerDocumentFromPayload(data)
          if (docStatus === 200 && doc && (doc.id || doc.document_id)) {
            const documentId = doc.id || doc.document_id
            const documentName = doc.name || signerData.document_name || "documento"

            resolvePostVerificationStatus({
              document: doc,
              signer: signerData,
              loadDocument: () =>
                Api("signer/viewDocument")(signerAccessCode).then(
                  ({ data: { data: fullDocument } }) => fullDocument,
                ),
            }).then(({ document: statusDocument, status: terminalStatus }) => {
              if (terminalStatus) {
                navigate(`/sign/${signerAccessCode}`, {
                  state: {
                    verified: true,
                    dataConfirmed: true,
                    terminalDocument: statusDocument,
                    terminalStatus,
                    verification_method: "WhatsApp",
                  },
                  replace: true,
                })
                return
              }

              const nextStep = getNextStep(signerData)

              Promise.resolve(nextStep).then((path) => {
                navigate(path, {
                  state: {
                    verified: true,
                    documentId: documentId,
                    documentName: documentName,
                    dataConfirmed: false,
                    ownerName: signerData.sender_name || "Proprietário do documento",
                    verification_method: "WhatsApp",
                  },
                  replace: true,
                })
              })
            })
          } else {
            navigate(`/sign/${signerAccessCode}`, {
              state: {
                verified: true,
                verification_method: "WhatsApp",
              },
              replace: true,
            })
          }
        })
        .catch(() => {
          setFormError({
            type: "warning",
            message: "Erro ao buscar documento",
          })
          setLoading(false)
        })
    } else {
      navigate(`/sign/${signerAccessCode}`, {
        state: {
          verified: true,
          verification_method: "WhatsApp",
        },
        replace: true,
      })
    }
  }, [signerAccessCode, navigate, getNextStep])

  useEffect(() => {
    let active = true

    const verifyWithCode = () => {
      if (!code || code.length !== 6 || !/^\d{6}$/.test(code)) {
        setFormError({
          type: "warning",
          message: "Código de verificação inválido",
        })
        setLoading(false)
        return
      }

      Api("signer/self")(signerAccessCode)
        .then(({ status, data: { data } }) => {
          if (!active) return
          if (status === 200) {
            setSigner(data)

            if (data.verification_method === "Bypass") {
              handleBypassFlow(data)
              return
            }

            // Os métodos de identidade não usam código de e-mail/WhatsApp. Mesmo
            // que uma URL antiga com código seja aberta, siga para o fluxo certo.
            if (isIdentityVerificationMethod(data.verification_method)) {
              navigate(`/verify/${signerAccessCode}`, { replace: true })
              return
            }

            Api("signer/verifyCode")(signerAccessCode, code)
              .then((resp) => {
                if (resp.status === 200) {
                  handleVerificationSuccess(data)
                } else {
                  setFormError({
                    type: "warning",
                    message: "Código de verificação inválido ou expirado",
                  })
                  setLoading(false)
                }
              })
              .catch(({ response }) => {
                setFormError({
                  type: "warning",
                  message: response?.data?.message || "Código de verificação inválido ou expirado",
                })
                setLoading(false)
              })
          }
        })
        .catch(() => {
          if (!active) return
          setFormError({
            type: "warning",
            message: "Erro ao buscar dados do signatário",
          })
          setLoading(false)
        })
    }

    Api("signer/viewDocument")(signerAccessCode)
      .then(({ status, data: { data } }) => {
        if (!active) return

        const terminalStatus = status === 200
          ? resolveSigningLinkStatus(data, data.current_signer)
          : null

        if (terminalStatus) {
          navigate(`/sign/${signerAccessCode}`, {
            state: {
              verified: true,
              dataConfirmed: true,
              terminalDocument: data,
              terminalStatus,
              verification_method: data.current_signer?.verification_method,
            },
            replace: true,
          })
          return
        }

        verifyWithCode()
      })
      .catch(() => {
        if (active) verifyWithCode()
      })

    return () => {
      active = false
    }
  }, [signerAccessCode, code, handleBypassFlow, handleVerificationSuccess])


  if (loading) {
    return (
      <Page className="page-sign">
        <Section>
          <Section.Header 
            title="Verificando..." 
            opening="Validando código de verificação" 
          />
        </Section>
      </Page>
    )
  }

  if (formError.message) {
    return (
      <Page className="page-sign">
        <Section>
          <Section.Header
            title="Erro na verificação"
            opening="Não foi possível verificar o código"
          />

          <Alert variant={formError.type} onClose={() => setFormError({})}>
            {formError.message}
          </Alert>

          <div style={{ marginTop: '20px' }}>
            <Link to={`/verify/${signerAccessCode}`}>
              Tentar novamente
            </Link>
          </div>
        </Section>
      </Page>
    )
  }

  return null
}

export default SignerVerificationWithCode
